import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import useFeatureToggle from 'hooks/useFeatureToggle';
import { LinkButton, Spinner } from 'xm-spark';

import { HELP_TABS } from 'xm-constants/helpView';
import { showHelp } from 'actions/helpPopover';
import { setInstallMobileAppDialogOpen } from 'actions/installDevice';
import { useHelpLinks } from 'Application/HelpLinks/HelpLinksProvider';
import XMLink from 'components/XMLink';
import CheckIcon from 'resources/svg/icon-indicator-success.svg';
import { getLoggedInUser } from 'selectors/loggedInUser';

import { useUpdateUserQuickStartProgress } from '../../hooks';
import styles from './QuickStartActionSection.module.scss';

export default function QuickStartActionSection({
  completed,
  isLoading,
  section,
  onQuickStartActionClick,
  testIdPrefix,
}) {
  const {
    sectionId,
    callToActionId,
    callToActionRoute,
    helpKey,
    isDeviceAction,
  } = section;
  const { helpLinks } = useHelpLinks();
  const helpLink = helpLinks[helpKey];
  const dispatch = useDispatch();
  const [mutateUpdateUserQuickStart] = useUpdateUserQuickStartProgress();

  const loggedInUser = useSelector(getLoggedInUser);
  const isCompleted = completed.includes(sectionId);
  const isReactUserProfileEnabled = useFeatureToggle('REACT_USER_PROFILE_MVP');

  const handleClick = () => {
    onQuickStartActionClick();
    mutateUpdateUserQuickStart(sectionId);
    dispatch(showHelp(helpLink, { tab: HELP_TABS.HELP }));

    // If `isDeviceAction` is true, dispatch an action to open
    // open the Mobile Device dialog box on the Devices page
    if (isDeviceAction) {
      dispatch(setInstallMobileAppDialogOpen(true));
    }
  };

  if (!isCompleted && !isLoading) {
    return (
      <LinkButton
        color="primary"
        onClick={handleClick}
        to={callToActionRoute(isReactUserProfileEnabled, loggedInUser)}
        LinkComponent={XMLink}
        data-testid={`${testIdPrefix}__actionButton--${sectionId}`}
      >
        <FormattedMessage id={callToActionId} />
      </LinkButton>
    );
  }

  if (!!isCompleted && !isLoading) {
    return (
      <XMLink
        className={styles.checkIconLink}
        onClick={handleClick}
        to={callToActionRoute(isReactUserProfileEnabled, loggedInUser)}
        data-testid={`${testIdPrefix}__actionButton--${sectionId}`}
      >
        <CheckIcon
          className={styles.checkIcon}
          data-testid={`${testIdPrefix}__checkIcon--${sectionId}`}
        />
      </XMLink>
    );
  }

  return <Spinner />;
}

QuickStartActionSection.propTypes = {
  completed: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  section: PropTypes.shape().isRequired,
  onQuickStartActionClick: PropTypes.func.isRequired,
  testIdPrefix: PropTypes.string.isRequired,
};

QuickStartActionSection.displayName = 'QuickStartActionSection';
